/**
 * Central mapping for context-aware global header search.
 * Each module defines route, placeholder, URL patterns, and suggestion settings.
 */

export const GLOBAL_SEARCH_QUERY_PARAM = 'q';

/** Legacy alias supported by listing pages and external links. */
export const GLOBAL_SEARCH_KEYWORD_PARAM = 'keyword';

/** @typedef {'products'|'auctions'|'contractManufacturers'|'serviceProviders'|'infrastructureLeasing'|'buyLeads'|'sellLeads'|'suppliers'|'companies'} SearchModuleId */

/**
 * @typedef {Object} SearchModuleConfig
 * @property {SearchModuleId} id
 * @property {string} placeholder
 * @property {string} route
 * @property {RegExp[]} pathPatterns
 * @property {boolean} [suggestionsEnabled]
 * @property {string} [suggestSource] descriptive hint for maintainers
 */

/** @type {SearchModuleConfig[]} */
export const SEARCH_MODULES = [
  {
    id: 'products',
    placeholder: 'Search products...',
    route: '/marketplace/products',
    pathPatterns: [/^\/marketplace\/products(?:\/|$)/i, /^\/home(?:\/|$)/i],
    suggestionsEnabled: true,
    suggestSource: 'hub marketplace API',
  },
  {
    id: 'auctions',
    placeholder: 'Search auction products...',
    route: '/marketplace/auctions',
    pathPatterns: [/^\/marketplace\/auctions(?:\/|$)/i, /^\/auction(?:\/|$)/i],
    suggestionsEnabled: true,
    suggestSource: 'active auctions API',
  },
  {
    id: 'contractManufacturers',
    placeholder: 'Search contract manufacturers...',
    route: '/contract-manufacturers',
    pathPatterns: [/^\/contract-manufacturers(?:\/|$)/i],
    suggestionsEnabled: true,
    suggestSource: 'contract manufacturers list API',
  },
  {
    id: 'serviceProviders',
    placeholder: 'Search service providers...',
    route: '/logistics-providers',
    pathPatterns: [/^\/logistics-providers(?:\/|$)/i, /^\/service-providers(?:\/|$)/i],
    suggestionsEnabled: true,
    suggestSource: 'logistics providers list API',
  },
  {
    id: 'infrastructureLeasing',
    placeholder: 'Search infrastructure listings...',
    route: '/infrastructure-leasing/listings',
    pathPatterns: [/^\/infrastructure-leasing(?:\/|$)/i],
    suggestionsEnabled: true,
    suggestSource: 'published infrastructure listings API',
  },
  {
    id: 'buyLeads',
    placeholder: 'Search buy leads...',
    route: '/buy-leads',
    pathPatterns: [/^\/buy-leads(?:\/|$)/i],
    suggestionsEnabled: false,
  },
  {
    id: 'sellLeads',
    placeholder: 'Search sell leads...',
    route: '/sell-leads',
    pathPatterns: [/^\/sell-leads(?:\/|$)/i],
    suggestionsEnabled: false,
  },
  {
    id: 'suppliers',
    placeholder: 'Search suppliers...',
    route: '/suppliers',
    pathPatterns: [/^\/suppliers(?:\/|$)/i],
    suggestionsEnabled: false,
  },
  {
    id: 'companies',
    placeholder: 'Search companies...',
    route: '/companies',
    pathPatterns: [/^\/companies(?:\/|$)/i],
    suggestionsEnabled: false,
  },
];

/** @type {Record<SearchModuleId, { placeholder: string, route: string, suggestionsEnabled: boolean, suggestSource?: string }>} */
export const SEARCH_CONFIG = Object.fromEntries(
  SEARCH_MODULES.map((module) => [
    module.id,
    {
      placeholder: module.placeholder,
      route: module.route,
      suggestionsEnabled: module.suggestionsEnabled !== false,
      suggestSource: module.suggestSource,
    },
  ]),
);

const MODULE_BY_ID = Object.fromEntries(SEARCH_MODULES.map((m) => [m.id, m]));

/** Default when no module path matches (home, generic hub pages). */
export const DEFAULT_SEARCH_MODULE_ID = 'products';

/** Primary scope tabs shown above the header search (Alibaba-style module switcher). */
export const HEADER_SEARCH_TAB_IDS = [
  'products',
  'auctions',
  'contractManufacturers',
  'serviceProviders',
  'infrastructureLeasing',
];

/** Short labels for scope tabs. */
export const SEARCH_MODULE_TAB_LABELS = {
  products: 'Products',
  auctions: 'Auctions',
  contractManufacturers: 'Manufacturers',
  serviceProviders: 'Services',
  infrastructureLeasing: 'Leasing',
  buyLeads: 'Buy Leads',
  sellLeads: 'Sell Leads',
  suppliers: 'Suppliers',
  companies: 'Companies',
};

/**
 * @param {string} pathname
 * @returns {SearchModuleConfig}
 */
export function resolveSearchModule(pathname) {
  const path = String(pathname || '/');
  for (const module of SEARCH_MODULES) {
    if (module.pathPatterns.some((pattern) => pattern.test(path))) {
      return module;
    }
  }
  return MODULE_BY_ID[DEFAULT_SEARCH_MODULE_ID];
}

/**
 * @param {URLSearchParams|string} searchParams
 * @returns {string}
 */
export function readSearchQueryFromParams(searchParams) {
  const params =
    searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || '');
  return (params.get(GLOBAL_SEARCH_QUERY_PARAM) || params.get(GLOBAL_SEARCH_KEYWORD_PARAM) || '').trim();
}

/**
 * Build listing URL for a module search submission.
 * Preserves existing query params when staying on the same module family.
 *
 * @param {SearchModuleConfig} module
 * @param {string} query
 * @param {URLSearchParams} currentSearchParams
 * @param {string} currentPathname
 */
export function buildModuleSearchUrl(module, query, currentSearchParams, currentPathname) {
  const onSameModule = module.pathPatterns.some((pattern) => pattern.test(currentPathname || ''));
  const next = new URLSearchParams(onSameModule ? currentSearchParams : undefined);

  const trimmed = String(query || '').trim();
  if (trimmed) {
    next.set(GLOBAL_SEARCH_QUERY_PARAM, trimmed);
    next.set(GLOBAL_SEARCH_KEYWORD_PARAM, trimmed);
  } else {
    next.delete(GLOBAL_SEARCH_QUERY_PARAM);
    next.delete(GLOBAL_SEARCH_KEYWORD_PARAM);
  }
  next.delete('page');

  const qs = next.toString();
  return qs ? `${module.route}?${qs}` : module.route;
}

export function getSearchModuleById(id) {
  return MODULE_BY_ID[id] || MODULE_BY_ID[DEFAULT_SEARCH_MODULE_ID];
}
